/**
 * Central config for on-page tutorials.
 *
 * Adds a new tutorial for a space? Drop it here — no changes to individual
 * page files. Each entry becomes: <PageTutorial config={TUTORIALS[key]} />
 *
 * Copy is honest and capability-based per the HomeLyph Pro brand tone
 * (no fake numbers, no marketing filler).
 */
export const TUTORIALS = {
  dashboard: {
    storageKey: 'tour-dashboard-v1',
    title: 'How the dashboard works',
    reopenLabel: 'Dashboard tour',
    steps: [
      { title: '1 · One tap to every tool', body: 'The row of amber pills below the header is your quick-jump menu. It sticks to the top of every member page.' },
      { title: '2 · Pick up where you left off', body: 'Recent lessons, batches and plans surface in cards below. Nothing hidden three menus deep.' },
      { title: '3 · Complete your profile to unlock the badge', body: 'A short profile helps the AI tune its answers to your context. Head to My Portal to fill it in.' },
    ],
  },
  portal: {
    storageKey: 'tour-portal-v1',
    title: 'Your portal',
    reopenLabel: 'Portal tour',
    steps: [
      { title: '1 · Identity', body: 'Name, region, freeze dryer. This context is used by every tool on the site to tailor its output.' },
      { title: '2 · Watch the completion card', body: 'The right-rail card lists exactly which fields are still missing. Nothing blocks you from using tools — this is a soft nudge.' },
      { title: '3 · Achievements', body: 'Badges appear as you complete courses. Days-here and tier live in the greeting bar up top.' },
    ],
  },
  learning: {
    storageKey: 'tour-learning-v1',
    title: 'How Learning works',
    reopenLabel: 'Learning tour',
    steps: [
      { title: '1 · Pick a course', body: 'Courses are grouped by tier. Free lessons are free forever; Pro tiers unlock as you subscribe.' },
      { title: '2 · Work through the modules', body: 'Each course is broken into modules → lessons. Video, text, quizzes, checklists and polls all live inside a lesson.' },
      { title: '3 · Track your progress', body: 'Mark a lesson complete when you finish it. Course completion earns a badge that shows on your dashboard.' },
      { title: '4 · Discuss + ask', body: 'Every lesson has a threaded discussion at the bottom — ask questions and read what other operators noticed.' },
    ],
  },
  ai_chat: {
    storageKey: 'tour-ai-v1',
    title: 'Talking to the AI assistant',
    reopenLabel: 'AI tour',
    steps: [
      { title: '1 · Be specific', body: 'Tell it what you\'re trying to do — e.g. "planning a run of 12 lbs of strawberries on a medium freeze dryer". The more context, the better.' },
      { title: '2 · Reference your profile', body: 'The assistant already knows what machine you own and your experience level. Refer back to your batches, saved plans and recipes for tailored answers.' },
      { title: '3 · Verify before you act', body: 'AI output is a starting point, not a prescription. Cross-check safety-critical steps with an experienced operator or a reputable source.' },
    ],
  },
  videos: {
    storageKey: 'tour-videos-v1',
    title: 'Video Library',
    reopenLabel: 'Video Library tour',
    steps: [
      { title: '1 · Browse by topic', body: 'Videos are grouped by machine model, food category and skill level. Filters up top.' },
      { title: '2 · Save what you watch', body: 'Bookmark a video to come back to it. Bookmarks live on your dashboard.' },
      { title: '3 · Follow a series', body: 'Some videos are part of a series — the "next up" tile at the end takes you through the sequence.' },
    ],
  },
  community: {
    storageKey: 'tour-community-v1',
    title: 'Community',
    reopenLabel: 'Community tour',
    steps: [
      { title: '1 · Read the rules', body: 'Community norms are pinned in the top thread. Be specific, share context, be kind.' },
      { title: '2 · Ask real questions', body: 'Include your machine, food type, cycle length, and what you\'ve already tried. Vague questions get vague answers.' },
      { title: '3 · Follow and mute', body: 'You can follow a thread to see new replies, or mute it if it\'s not for you.' },
    ],
  },
  directory: {
    storageKey: 'tour-directory-v1',
    title: 'Partner directory',
    reopenLabel: 'Directory tour',
    steps: [
      { title: '1 · Vetted vendors only', body: 'Every partner in this list has applied and been reviewed. Filter by category — machines, packaging, consulting, etc.' },
      { title: '2 · Reach out directly', body: 'Each card links to the partner\'s own site. We don\'t take referral commissions on any listing.' },
      { title: '3 · Apply to be listed', body: 'If you run a service that fits, use the "Apply to be listed" button. Applications are reviewed manually.' },
    ],
  },
  recipes: {
    storageKey: 'tour-recipes-v1',
    title: 'Recipe Builder',
    reopenLabel: 'Recipe tour',
    steps: [
      { title: '1 · Start from a template or blank', body: 'Templates come from tested community batches. Blank gives you total control.' },
      { title: '2 · Ingredients and prep', body: 'Enter each ingredient with quantity and prep method. The tool calculates finished weight and moisture-loss estimates automatically.' },
      { title: '3 · Save + share', body: 'Save recipes to your account. Send a share link to another operator so they can clone and iterate.' },
    ],
  },
  store: {
    storageKey: 'tour-store-v1',
    title: 'Store',
    reopenLabel: 'Store tour',
    steps: [
      { title: '1 · Curated for freeze-dry', body: 'Only products relevant to freeze-drying: supplies, packaging, accessories, curated cookbooks.' },
      { title: '2 · Pricing in your currency', body: 'Prices auto-convert based on your region. You can lock a display currency in your portal.' },
      { title: '3 · Direct checkout', body: 'Checkout goes through Stripe. Your shipping and billing details are stored securely per-order.' },
    ],
  },
  business_calc: {
    storageKey: 'tour-business-calc-v1',
    title: 'How the Business Calculator works',
    reopenLabel: 'How to use this calculator',
    steps: [
      { title: '1 · Describe the product', body: 'Enter the plan / product name and a short description. This context helps the AI-driven market estimates below make sense — the more specific, the better.' },
      { title: '2 · Pick the machine + batch size', body: 'Choose your freeze dryer model and either pieces-per-tray or dry-weight per tray. The calculator uses this to work out units-per-batch automatically.' },
      { title: '3 · Log the costs', body: 'Ingredient, packaging, energy and any fixed overhead. Anything you leave blank is treated as zero — you can iterate as you learn your real numbers.' },
      { title: '4 · Read the results', body: 'Switch to the "Financial Results" tab. You get margin, break-even units, monthly capacity and a plain-language plan you can share with a partner or lender.' },
      { title: '5 · Save & share', body: 'Use "Save Plan" to store this to your portal, "Export PDF" to send to an accountant, or "New Plan" to start fresh.' },
    ],
  },
  household_calc: {
    storageKey: 'tour-household-calc-v1',
    title: 'How to plan a home batch',
    reopenLabel: 'How to use this calculator',
    steps: [
      { title: '1 · Pick your machine', body: 'Choose the freeze dryer model you use. Tray count + tray capacity come from that.' },
      { title: '2 · Add each food item', body: 'For each item enter what it is, how much (weight or count), and your cost per unit. The tool converts across grams / oz / lbs / kg for you.' },
      { title: '3 · Watch the tray load', body: 'You\'ll see a live "within capacity / high load / over capacity" hint per item so you can right-size the run.' },
      { title: '4 · Read the summary', body: 'Total cost, projected finished weight, estimated shelf life and packaging suggestions appear at the bottom. Save the batch to reuse the recipe later.' },
    ],
  },
  batch_tracker: {
    storageKey: 'tour-batch-tracker-v1',
    title: 'How to run a tracked batch',
    reopenLabel: 'How to use this tracker',
    steps: [
      { title: '1 · Start a new batch', body: 'Give the batch a name and pick your freeze dryer model. Tray count comes from the model automatically.' },
      { title: '2 · Assign items to trays', body: 'Add each item and tap the tray buttons to assign it to one or more trays. You can split large items across multiple trays.' },
      { title: '3 · Log each phase', body: 'As the machine progresses through freezing → primary → secondary drying, log timestamps and observations per tray so you can debug bad runs later.' },
      { title: '4 · Finish + review', body: 'Mark the batch complete when you package. All timings + notes stay under Batch Logs so you can compare runs over time.' },
    ],
  },
  calculators_hub: {
    storageKey: 'tour-calc-hub-v1',
    title: 'Using these tools',
    steps: [
      { title: '1 · Pick the right tool', body: 'Costing a batch for sale? Business Calculator. Planning a pantry run at home? Household Calculator. Monitoring a live cycle? Batch Tracker.' },
      { title: '2 · Everything stays on your account', body: 'Numbers you type are saved to your profile so you can come back later and refine them. Nothing is shared outside your account.' },
      { title: '3 · Outputs are estimates', body: 'These are educational tools. Cross-check any financial or production output with a qualified professional before making real business decisions.' },
    ],
  },
  admin_directory: {
    storageKey: 'tour-admin-directory-v1',
    title: 'Managing the directory',
    steps: [
      { title: '1 · Applications are reviewed here', body: 'New partner applications land in the "Applications" tab. Approve, request more info, or reject with a reason.' },
      { title: '2 · Every listing has a status', body: 'Verified, pending, or archived. Only "verified" listings appear in the public /directory.' },
      { title: '3 · Keep tags tight', body: 'Category + services drive filtering on the public page. Use existing tags where possible so users can filter cleanly.' },
    ],
  },
  admin_lms: {
    storageKey: 'tour-admin-lms-v1',
    title: 'Authoring courses',
    steps: [
      { title: '1 · Course → Module → Lesson', body: 'Each course groups modules. Each module groups lessons. Lessons are what learners actually watch/read/take quizzes on.' },
      { title: '2 · Content sections', body: 'Inside a lesson, mix and match video, text, PDF, markdown, presentation, quiz, checklist and poll sections. Drag to reorder.' },
      { title: '3 · Publish when ready', body: 'Draft courses stay hidden from learners. Toggle Published + Free/Tier gating from the course settings.' },
    ],
  },
  admin_users: {
    storageKey: 'tour-admin-users-v1',
    title: 'User moderation',
    steps: [
      { title: '1 · Search + filter', body: 'Filter by role (free / pro / admin) and status (active / suspended / banned). Search hits email and name.' },
      { title: '2 · Moderation actions', body: 'Suspend for temporary time-outs, ban for permanent removal, delete to wipe. Every action asks for a reason and is logged.' },
      { title: '3 · Promote to admin', body: 'Use "Promote" carefully — admin role gives access to every management screen. Demote back at any time.' },
    ],
  },
  admin_analytics: {
    storageKey: 'tour-admin-analytics-v1',
    title: 'Reading analytics',
    steps: [
      { title: '1 · Aggregate only', body: 'Numbers are counts and averages — no individual user data is exposed on this screen.' },
      { title: '2 · Survey-driven', body: 'Machine types, regions, experience levels come straight from onboarding survey responses.' },
      { title: '3 · Refresh on demand', body: 'This view is served from a materialised aggregate that updates as new surveys are submitted. Numbers can lag a survey submit by a few seconds.' },
    ],
  },
};

export default TUTORIALS;
